import React, { useEffect, useState } from "react";
import { AtSign, Bell, FileText, Heart, MessageCircle, Reply, UserPlus } from "lucide-react";

import { Header } from "../layout/Header";
import { Switch } from "../ui/switch";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import { PushOptInCard } from "./PushOptInCard";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { getStoredCurrentUser } from "../../lib/app-events";
import { showErrorToast } from "../../lib/toast";
import ApiService from "../../services/api-service";
import { CurrentUser } from "../../types/user";
import type { NotificationPreferences } from "../../types/social";

type TypeKey = Exclude<keyof NotificationPreferences, "pushEnabled">;

const TYPE_SETTINGS: { key: TypeKey; label: string; description: string; Icon: typeof Bell }[] = [
  { key: "newContent", label: "New posts from people you follow", description: "When someone you follow shares a quote, article, book, video or AI prompt.", Icon: FileText },
  { key: "follow", label: "New followers", description: "When someone starts following you.", Icon: UserPlus },
  { key: "like", label: "Likes", description: "When someone likes something you posted.", Icon: Heart },
  { key: "comment", label: "Comments", description: "When someone comments on your post.", Icon: MessageCircle },
  { key: "reply", label: "Replies", description: "When someone replies to your comment.", Icon: Reply },
  { key: "mention", label: "Mentions", description: "When someone mentions you with @username.", Icon: AtSign },
];

// /settings/notifications - push notification switches. The notification
// centre (the bell) still lists everything; these only control pushes.
export function NotificationSettingsPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [savingKey, setSavingKey] = useState<keyof NotificationPreferences | null>(null);

  useEffect(() => {
    document.title = "Notification settings · The Deepam";
  }, []);

  useEffect(() => {
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    setLoadError(false);
    ApiService.getNotificationPreferences()
      .then((response: { preferences?: NotificationPreferences }) => {
        if (!cancelled && response?.preferences) setPreferences(response.preferences);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, retryToken]);

  // Saves one switch right away, putting it back if the save fails.
  const handleToggle = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!preferences) return;
    const previous = preferences;
    setPreferences({ ...preferences, [key]: value });
    setSavingKey(key);
    try {
      const response = await ApiService.updateNotificationPreferences({ [key]: value });
      if (response?.preferences) setPreferences(response.preferences);
    } catch {
      setPreferences(previous);
      showErrorToast("Couldn't save that setting. Please try again.");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />
      <main className="mx-auto mt-6 max-w-2xl">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 sm:text-3xl">
          <Bell className="h-6 w-6 text-slate-400" aria-hidden="true" />
          Notifications
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Choose what The Deepam sends to your phone. Everything still appears in the notification centre.
        </p>

        {!currentUser?.id ? (
          <div className="mt-6 rounded-2xl border border-slate-200 p-6 text-center dark:border-slate-700">
            <p className="text-slate-700 dark:text-slate-200">Sign in to choose your notifications.</p>
            <Button className="mt-3 rounded-full" onClick={() => onOpenAuth?.("login")}>
              Sign in
            </Button>
          </div>
        ) : (
          <>
            <div className="-mx-4">
              <PushOptInCard variant="full" />
            </div>

            {loadError ? (
              <div className="mt-2 rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
                Couldn't load your settings.
                <div className="mt-3">
                  <Button variant="outline" size="sm" onClick={() => setRetryToken((n) => n + 1)}>
                    Try again
                  </Button>
                </div>
              </div>
            ) : !preferences ? (
              <div className="mt-2 space-y-3">
                {Array.from({ length: 7 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 w-full rounded-2xl" />
                ))}
              </div>
            ) : (
              <div className="mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <label className="flex cursor-pointer items-center gap-4 border-b border-slate-200 px-4 py-4 dark:border-slate-700">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                    <Bell className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-900 dark:text-slate-100">Push notifications</span>
                    <span className="block text-sm text-slate-500 dark:text-slate-400">
                      Turn every push notification on or off.
                    </span>
                  </span>
                  <Switch
                    checked={preferences.pushEnabled}
                    disabled={savingKey === "pushEnabled"}
                    onCheckedChange={(value: boolean) => handleToggle("pushEnabled", value)}
                    aria-label="Push notifications"
                  />
                </label>

                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {TYPE_SETTINGS.map(({ key, label, description, Icon }) => (
                    <li key={key}>
                      <label
                        className={`flex items-center gap-4 px-4 py-3.5 ${
                          preferences.pushEnabled ? "cursor-pointer" : "opacity-50"
                        }`}
                      >
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{label}</span>
                          <span className="block text-xs text-slate-500 dark:text-slate-400">{description}</span>
                        </span>
                        <Switch
                          checked={preferences[key]}
                          disabled={!preferences.pushEnabled || savingKey === key}
                          onCheckedChange={(value: boolean) => handleToggle(key, value)}
                          aria-label={label}
                        />
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
